package app.alefly.quizengine

import app.alefly.domain.model.ChallengeId
import app.alefly.domain.model.Difficulty
import app.alefly.domain.model.Question
import app.alefly.domain.model.QuestionId
import app.alefly.domain.model.QuizId
import app.alefly.domain.model.TenantId
import kotlinx.datetime.Instant
import kotlinx.serialization.Serializable

@Serializable
enum class QuizSessionStatus {
    IDLE,
    LOADING,
    IN_PROGRESS,
    ANSWER_FEEDBACK,
    COMPLETED,
    SUBMITTING,
    SUBMITTED,
    ERROR,
}

@Serializable
data class QuizAnswer(
    val questionId: QuestionId,
    val selectedOptionId: String,
    val usedHint: Boolean = false,
    val answeredAt: Instant? = null,
)

@Serializable
data class QuizSessionState(
    val challengeId: ChallengeId,
    val tenantId: TenantId,
    val quizId: QuizId,
    val locale: String,
    val difficulty: Difficulty,
    val questionIds: List<QuestionId>,
    val currentIndex: Int = 0,
    val answers: List<QuizAnswer> = emptyList(),
    val usedHints: List<QuestionId> = emptyList(),
    val startedAt: Instant? = null,
    val finishedAt: Instant? = null,
    val status: QuizSessionStatus = QuizSessionStatus.IDLE,
)

@Serializable
data class QuizScorePreview(
    val answeredCount: Int,
    val correctCount: Int,
    val hintsUsedCount: Int,
    val previewScore: Int,
)

object QuizSessionEngine {
    fun create(
        challengeId: ChallengeId,
        tenantId: TenantId,
        quizId: QuizId,
        locale: String,
        difficulty: Difficulty,
        questionIds: List<QuestionId>,
    ): QuizSessionState = QuizSessionState(
        challengeId = challengeId,
        tenantId = tenantId,
        quizId = quizId,
        locale = locale,
        difficulty = difficulty,
        questionIds = questionIds,
        status = QuizSessionStatus.IDLE,
    )

    fun start(
        state: QuizSessionState,
        startedAt: Instant,
    ): QuizSessionState = state.copy(
        startedAt = state.startedAt ?: startedAt,
        status = QuizSessionStatus.IN_PROGRESS,
    )

    fun revealHint(state: QuizSessionState): QuizSessionState {
        val currentQuestionId = state.currentQuestionId() ?: return state
        if (currentQuestionId in state.usedHints) {
            return state
        }
        if (state.answers.any { it.questionId == currentQuestionId }) {
            return state
        }

        return state.copy(usedHints = state.usedHints + currentQuestionId)
    }

    fun answerCurrentQuestion(
        state: QuizSessionState,
        selectedOptionId: String,
        answeredAt: Instant,
    ): QuizSessionState {
        val currentQuestionId = state.currentQuestionId() ?: return state
        if (state.answers.any { it.questionId == currentQuestionId }) {
            return state
        }

        val answer = QuizAnswer(
            questionId = currentQuestionId,
            selectedOptionId = selectedOptionId,
            usedHint = currentQuestionId in state.usedHints,
            answeredAt = answeredAt,
        )

        return state.copy(
            answers = state.answers + answer,
            status = QuizSessionStatus.ANSWER_FEEDBACK,
        )
    }

    fun goToNextQuestion(
        state: QuizSessionState,
        finishedAt: Instant? = null,
    ): QuizSessionState {
        val currentQuestionId = state.currentQuestionId()
        if (currentQuestionId != null && state.answers.none { it.questionId == currentQuestionId }) {
            return state
        }

        val nextIndex = state.currentIndex + 1
        if (nextIndex >= state.questionIds.size) {
            return state.copy(
                currentIndex = state.questionIds.lastIndex.coerceAtLeast(0),
                finishedAt = finishedAt ?: state.finishedAt,
                status = QuizSessionStatus.COMPLETED,
            )
        }

        return state.copy(
            currentIndex = nextIndex,
            status = QuizSessionStatus.IN_PROGRESS,
        )
    }

    fun markSubmitting(state: QuizSessionState): QuizSessionState = state.copy(
        status = QuizSessionStatus.SUBMITTING,
    )

    fun markSubmitted(state: QuizSessionState): QuizSessionState = state.copy(
        status = QuizSessionStatus.SUBMITTED,
    )

    fun fail(state: QuizSessionState, finishedAt: Instant? = null): QuizSessionState = state.copy(
        finishedAt = finishedAt ?: state.finishedAt,
        status = QuizSessionStatus.ERROR,
    )

    /**
     * Estima o score da sessão NO CLIENTE, com a mesma fórmula de bônus por velocidade do
     * backend — para a UI mostrar o placar provisório sem ida ao servidor. O score oficial
     * continua sendo o do backend na submissão; este é só preview.
     *
     * Por questão CORRETA, os pontos seguem `computeSpeedBonusPoints`
     * (backend `support/constants.ts`): `round(minPts + (maxPts - minPts) * ratio)`, onde
     * `ratio = remaining / total`, `remaining = max(0, total - elapsed)`,
     * `total = answerExpirationSeconds * 1000`. Quanto mais rápido, mais perto de [maxPointsPerQuestion];
     * estourado o tempo, [minPointsPerQuestion]. Questão errada não pontua.
     *
     * PARIDADE com `submitChallengeResult` (anchor da medição de tempo): o backend mede
     * `elapsed = gradedAt[i] - anchor`, com `anchor = i == 0 ? startedAt : gradedAt[i-1]` —
     * isto é, o relógio de cada questão começa quando a ANTERIOR foi respondida, não no
     * início da rodada. O cliente reproduz isso via [questionStartTimesMs] (o caller deve
     * popular o índice i com o `answeredAt` da questão i-1, e o índice 0 com `startedAt`).
     * Quando o start/answer não está disponível, cai em [maxPointsPerQuestion] (mesma rede
     * de segurança otimista do caminho sem timestamps).
     *
     * @param questionStartTimesMs por índice de resposta, o instante-âncora (ms epoch) a
     *   partir do qual o tempo daquela questão conta; vazio desativa o bônus por velocidade.
     */
    fun previewScore(
        state: QuizSessionState,
        questions: List<Question>,
        hintPenaltyValue: Int,
        answerExpirationSeconds: Int = 30,
        maxPointsPerQuestion: Int = 100,
        minPointsPerQuestion: Int = 10,
        questionStartTimesMs: List<Long> = emptyList(),
    ): QuizScorePreview {
        val questionsById = questions.associateBy(Question::id)
        val gradedAnswers = state.answers.map { answer ->
            val question = questionsById[answer.questionId]
            QuizGradedAnswer(
                questionId = answer.questionId,
                selectedOptionId = answer.selectedOptionId,
                isCorrect = question?.options?.any { option ->
                    option.id == answer.selectedOptionId && option.isCorrect
                } == true,
                usedHint = answer.usedHint,
                answeredAtEpochMs = answer.answeredAt?.toEpochMilliseconds(),
            )
        }
        return QuizScoreEngine.preview(
            answers = gradedAnswers,
            questionStartTimesMs = questionStartTimesMs,
            answerExpirationSeconds = answerExpirationSeconds,
            maxPointsPerQuestion = maxPointsPerQuestion,
            minPointsPerQuestion = minPointsPerQuestion,
        )
    }

    private fun QuizSessionState.currentQuestionId(): QuestionId? = questionIds.getOrNull(currentIndex)
}
